import React, { useState, useRef, useEffect } from 'react'
import { useAtom } from 'jotai'
// import { ArrowUp } from 'lucide-react'
import { currentUserAtom } from '@/stores/sessionStore'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { Button, ScrollArea } from '@deepguide-ai/ui'
import { CommentEditor } from './comment-editor/comment-editor'
import { CommentThread } from './comment-thread'
import type { Comment } from './types'

interface CommentPanelProps {
  blockId: string
  comments: Comment[]
  onAddComment: (content: string, extra?: { mentions?: string[]; richContent?: any }) => void
  onReply: (parentId: string, content: string) => void
  onResolve: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onClose?: () => void
  rangeText?: string // The text that is being commented on
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  onEditComment?: (commentId: string, content: string) => void
  onDeleteComment?: (commentId: string) => void
  isRangeComment?: boolean // Flag to indicate if this is a range comment panel
  isAddingComment?: boolean // Flag to indicate this is a new comment panel
  // 仅展示内容（隐藏回复与操作按钮）。用于 block 右侧按钮弹窗
  showContent?: boolean
  hideComposer?: boolean
  // 新增：浮窗点击线程时，通知上层触发右侧面板跳转高亮
  onThreadActivate?: (commentId: string) => void
  // Optional pre-fetched members for mentions (passed to CommentEditor)
  members?: Array<{ id: string; name?: string | null; email?: string | null }>
}

export function CommentPanel({
  blockId: _blockId, // Not used directly but kept for API compatibility
  comments,
  onAddComment,
  onReply,
  onResolve,
  onClose,
  rangeText,
  onApplySuggestion,
  onDismissCheck,
  onEditComment,
  onDeleteComment,
  isRangeComment = false,
  isAddingComment = false,
  showContent = false,
  hideComposer = false,
  onThreadActivate,
  members = [],
}: CommentPanelProps) {
  // Completed auditor cycles are view-only; other block popovers keep the composer.

  const [currentUser] = useAtom(currentUserAtom)
  const isBlockPanel = _blockId && !isRangeComment
  const showNewCommentInput =
    !(showContent && hideComposer) &&
    (isBlockPanel || isAddingComment || (!rangeText && comments.length === 0))

  const [newCommentContent, setNewCommentContent] = useState('')
  const newCommentRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // 直接在 panelRef 内部查询 ScrollArea 视口

  useEffect(() => {
    // Focus on new comment input if no comments exist
    if (comments.length === 0 && newCommentRef.current) {
      newCommentRef.current.focus()
    }
  }, [comments.length])

  const prevCountsRef = useRef<{ replies: number; threads: number }>({
    replies: 0,
    threads: 0,
  })
  // 首次数据加载时不触发滚动
  const hasInitializedDataRef = useRef(false)
  useEffect(() => {
    const vp = panelRef.current?.querySelector(
      '[data-slot="scroll-area-viewport"]'
    ) as HTMLElement | null

    const threadCount = comments.filter((c) => !c.parentId)?.length ?? 0
    const replyCount = comments.filter((c) => c.commentType === 'REPLY').length

    // 初次渲染或首次数据加载时，不滚动，仅建立基线
    if (!hasInitializedDataRef.current) {
      prevCountsRef.current = { replies: replyCount, threads: threadCount }
      if (comments.length > 0) {
        hasInitializedDataRef.current = true
      }
      return
    }

    const prev = prevCountsRef.current
    // 仅当计数增加时才滚动，若计数相同或减少则不滚动
    const countsIncreased =
      replyCount > prev.replies || threadCount > prev.threads
    if (!countsIncreased) {
      return
    }

    const replyIncreased = replyCount > prev.replies
    const multipleThreads = threadCount > 1

    if (vp && vp.scrollHeight > vp.clientHeight) {
      if (!replyIncreased || !multipleThreads) {
        vp.scrollTo({ top: vp.scrollHeight, behavior: 'auto' })
      }
    }

    prevCountsRef.current = { replies: replyCount, threads: threadCount }
  }, [comments])

  // Outside clicks are now handled by Floating UI in the parent component

  const handleAddComment = () => {
    if (newCommentContent.trim()) {
      onAddComment(newCommentContent.trim())
      setNewCommentContent('')
    }
  }

  // Group comments by thread (parent and replies)
  const threads = React.useMemo(() => {
    const parentComments = comments.filter((c) => !c.parentId)
    return parentComments.map((parent) => ({
      parent,
      replies: comments.filter((c) => c.parentId === parent.id),
    }))
  }, [comments])

  return (
    <div
      ref={panelRef}
      className='comment-panel border-border bg-card w-[360px] border border-solid'
      style={{
        borderRadius: 'var(--radius-md)',
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        maxHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ScrollArea
        className='h-full w-full'
        type='hover'
        viewClassName='[&>div]:!block h-full w-[450px] max-h-[400px] min-h-[48px]'
      >
        {/* Show the commented text for new range comments only */}
        {rangeText && isAddingComment && comments.length === 0 && (
          <div className='w-full p-3 pb-0'>
            <div
              className='text-muted-foreground mb-1.5 w-full pl-3 text-[13px] leading-[18px]'
              style={{ borderLeft: '2px solid rgba(251, 191, 36, 0.5)' }}
            >
              <Tooltip>
                <TooltipTrigger className='w-fit max-w-full truncate text-left'>
                  "{rangeText}"
                </TooltipTrigger>
                <TooltipContent className='max-w-[400px]'>
                  "{rangeText}"
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Existing comments */}
        {threads.length > 0 && (
          <div className='comment-threads w-[450px]'>
            {threads.map(({ parent, replies }, index) => (
              <CommentThread
                key={parent.id}
                parent={parent}
                replies={replies}
                onReply={onReply}
                onResolve={onResolve}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
                onApplySuggestion={onApplySuggestion}
                onDismissCheck={onDismissCheck}
                showSeparator={index > 0}
                // 仅展示内容：隐藏回复输入与操作按钮
                showContent={showContent}
                hideComposer={hideComposer}
                members={members}
                onActivate={() => onThreadActivate?.(parent.id)}
              />
            ))}
          </div>
        )}

        {/* New comment input - show for block comments and when adding new range comment */}
        {showNewCommentInput && (
          <div className='comment-new'>
            {threads.length > 0 && (
              <div className='h-[1px] w-full bg-[var(--border)]' />
            )}
            <div className='comment-new-wrapper mt-2 mb-2.5 flex px-3'>
              {/* <div className='comment-avatar comment-new-avatar'>
                <div className='comment-avatar-placeholder'>T</div>
              </div> */}
              <MemberAvatar
                person={currentUser}
                size='sm'
                className='mt-1'
              />
              <div className='relative flex-1'>
                <CommentEditor
                  members={members}
                  autoFocus={threads.length === 0}
                  placeholder={threads.length > 0 ? 'Add a comment…' : 'Write a comment…'}
                  maxHeightClass='max-h-32'
                  onSubmit={({ text, json, mentions }) => {
                    setNewCommentContent('')
                    onAddComment(text, { richContent: json, mentions })
                  }}
                  onCancel={() => {
                    onClose?.()
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
